import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Coins, Package, Plus, Receipt, ListChecks } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtEUR, nowPeriod } from "@/lib/format";
import { ChannelBadge, Loading, PageHeader, PeriodSelect, TipoBadge, btnGhost, btnPrimary } from "@/components/app/common";
import { KpiCard } from "@/components/app/KpiCard";
import { useAuth } from "@/context/AuthContext";
import { StockAlert } from "@/components/app/StockAlert";

export default function CollabDashboard() {
  const { user } = useAuth();
  const [period, setPeriod] = useState(nowPeriod());
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/riepilogo", { params: period }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [period]);

  return (
    <div data-testid="collab-dashboard">
      <PageHeader overline={`Ciao ${user?.name?.split(" ")[0] || ""}`} title="Riepilogo" subtitle="I totali del mese e le ultime registrazioni.">
        <PeriodSelect anno={period.anno} mese={period.mese} onChange={(p) => setPeriod({ ...p, mese: p.mese || 1 })} prefix="riepilogo" />
      </PageHeader>
      {!data ? <Loading /> : (
        <div className="space-y-6">
          <StockAlert />
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5">
            <KpiCard testId="kpi-card-incassi" label="Incassi (imponibile)" value={fmtEUR(data.incassi)} delta={data.delta.incassi} prevLabel={data.periodo.prev_label} icon={Coins} tone="sage" />
            <KpiCard testId="kpi-card-costi" label="Costi (imponibile)" value={fmtEUR(data.costi)} delta={data.delta.costi} prevLabel={data.periodo.prev_label} icon={Receipt} tone="terra" invert delay={60} />
            <KpiCard testId="kpi-card-movimenti" label="Movimenti registrati" value={data.n_movimenti} icon={ListChecks} tone="blush" delay={120} />
            <KpiCard testId="kpi-card-unita" label="Pezzi venduti" value={data.unita_vendute} icon={Package} tone="gold" sub="Da Vendite mensili" delay={180} />
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/movimenti?nuovo=1" data-testid="quick-add-movimento" className={`${btnPrimary} inline-flex items-center gap-2`}><Plus className="h-4 w-4" />Nuovo movimento</Link>
            <Link to="/vendite-mensili" data-testid="quick-vendite" className={`${btnGhost} border inline-flex items-center px-5`}>Inserisci vendite del mese</Link>
          </div>
          <section className="card-soft p-5 sm:p-7" data-testid="recent-movimenti">
            <h2 className="text-lg font-medium mb-4">Ultimi movimenti</h2>
            {!data.recenti.length ? <p className="text-sm text-[#756B64]">Nessun movimento ancora registrato.</p> : (
              <ul className="divide-y divide-[#EAE3DC]">
                {data.recenti.map((m) => (
                  <li key={m.id} className="py-3 flex items-center gap-3 flex-wrap">
                    <span className="text-xs text-[#756B64] w-20 tabular">{fmtDate(m.data)}</span>
                    <TipoBadge tipo={m.tipo} ricorrente={!!m.ricorrente_id} />
                    <span className="text-sm flex-1 min-w-[140px] truncate">{m.descrizione || m.categoria}</span>
                    <ChannelBadge canale={m.canale} />
                    <span className="text-sm font-semibold tabular w-28 text-right">{fmtEUR(m.imponibile)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
